import { useEffect } from 'react';
import { connect } from 'react-redux';

import { isLoading, myProfile } from 'Redux/user-profile/selectors';
import useTracking from 'Util/Analytics/useTracking';
import { genVideoEvent, getEventLabel, VIDEO_ACTIONS } from 'Util/Analytics';
import { secondsToTime } from 'Util/datetime';
import { useClassroomContext } from 'Context/ClassroomContext';
import { useVideoPlayer } from 'Context/VideoPlayer';
import { CUSTOM_PLAYER_EVENT } from 'Components/VideoPlayer/components';

const ELEMENT_TYPE = {
  PLAYER: 'PLAYER',
  QUALITY_LEVELS: 'QUALITY_LEVELS',
};

const VideoAnalyticListener = ({ user }) => {
  const { logAction } = useTracking();
  const { player } = useVideoPlayer();
  const { courseData, currentSubsectionId } = useClassroomContext();

  useEffect(() => {
    const qualityLevels = player?.qualityLevels();
    const events = [
      {
        elementType: ELEMENT_TYPE.PLAYER,
        type: 'play',
        listener: () => {
          logAction(
            genVideoEvent(VIDEO_ACTIONS.PLAY, getEventLabel(courseData, currentSubsectionId))
          );
        },
      },
      {
        elementType: ELEMENT_TYPE.PLAYER,
        type: 'seeked',
        listener: () => {
          logAction(
            genVideoEvent(VIDEO_ACTIONS.SEEK, getEventLabel(courseData, currentSubsectionId))
          );
        },
      },
      {
        elementType: ELEMENT_TYPE.PLAYER,
        type: 'pause',
        listener: () => {
          logAction(
            genVideoEvent(VIDEO_ACTIONS.PAUSE, getEventLabel(courseData, currentSubsectionId))
          );
        },
      },
      {
        elementType: ELEMENT_TYPE.PLAYER,
        type: 'ratechange',
        listener: () => {
          logAction(
            genVideoEvent(
              VIDEO_ACTIONS.SPEED,
              getEventLabel(
                courseData,
                currentSubsectionId,
                {
                  title: 'speed',
                  value: player.playbackRate(),
                },
                { title: 'userId', value: user.id },
                {
                  title: 'vTime',
                  value: secondsToTime(player.currentTime()),
                }
              )
            )
          );
        },
      },
      {
        elementType: ELEMENT_TYPE.QUALITY_LEVELS,
        type: 'change',
        listener: () => {
          logAction(
            genVideoEvent(
              VIDEO_ACTIONS.SETTING,
              getEventLabel(courseData, currentSubsectionId, {
                title: 'setting',
                value: qualityLevels[qualityLevels.selectedIndex].height,
              })
            )
          );
        },
      },
      {
        elementType: ELEMENT_TYPE.PLAYER,
        type: CUSTOM_PLAYER_EVENT.BACKWARD,
        listener: () => {
          logAction(
            genVideoEvent(VIDEO_ACTIONS.PREVIOUS, getEventLabel(courseData, currentSubsectionId))
          );
        },
      },
      {
        elementType: ELEMENT_TYPE.PLAYER,
        type: CUSTOM_PLAYER_EVENT.FORWARD,
        listener: () => {
          logAction(
            genVideoEvent(VIDEO_ACTIONS.NEXT, getEventLabel(courseData, currentSubsectionId))
          );
        },
      },
    ];

    if (player && typeof player.on === 'function') {
      for (const event of events) {
        if (event.elementType === ELEMENT_TYPE.PLAYER) {
          player.on(event.type, event.listener);
        } else if (event.elementType === ELEMENT_TYPE.QUALITY_LEVELS) {
          qualityLevels.on(event.type, event.listener);
        }
      }
    }

    return () => {
      if (player && typeof player.off === 'function') {
        // This is workaround to prevent remain event when hot reload
        for (const event of events) {
          if (event.elementType === ELEMENT_TYPE.PLAYER) {
            player.off(event.type, event.listener);
          } else if (event.elementType === ELEMENT_TYPE.QUALITY_LEVELS) {
            qualityLevels.off(event.type, event.listener);
          }
        }
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player]);

  return null;
};

export default connect(
  (state) => ({
    user: myProfile(state),
    isLoading: isLoading(state),
  }),
  {}
)(VideoAnalyticListener);
